import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Sun, Moon } from "lucide-react";
import { calculatePrayerTimes } from "@/lib/prayer/calc";
import { type Location, tzOffsetHours } from "@/data/locations";
import { todayISO, parseISO } from "@/lib/calendar/dates";
import type { PrayerVariant } from "@/lib/seo/prayer-schema";

function shortPath(variant: PrayerVariant, loc: Location): string {
  const base = variant === "buka-puasa" ? "/jadwal-buka-puasa" : `/jadwal-${variant}`;
  if (loc.kind === "kecamatan") return `${base}/${loc.parentSlug}/${loc.slug}`;
  return `${base}/${loc.slug}`;
}

/**
 * Rich pSEO grid of sibling locations with today's Subuh + Maghrib preview.
 * Used on a kota page (lists kecamatan) or province page (lists kota).
 */
export function RelatedAreaGrid({
  variant,
  parent,
  items,
  childKind,
}: {
  variant: PrayerVariant;
  parent: Location;
  items: Location[];
  childKind: "kota" | "kecamatan";
}) {
  if (items.length === 0) return null;
  const INITIAL = 24;
  const [showAll, setShowAll] = useState(items.length <= INITIAL);
  const today = parseISO(todayISO())!;
  const visible = showAll ? items : items.slice(0, INITIAL);
  const remaining = items.length - visible.length;
  const childLabel = childKind === "kecamatan" ? "Kecamatan" : "Kota / Kabupaten";
  const variantLabel = variant === "sholat" ? "Jadwal sholat" : variant === "imsak" ? "Jadwal imsakiyah" : "Jadwal buka puasa";

  // Aggregate JSON-LD ItemList for SEO
  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${childLabel} di ${parent.name}`,
    itemListElement: items.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://hari.co.id${shortPath(variant, c)}/`,
      name: c.name,
    })),
  };

  return (
    <section className="mt-6">
      <div className="mb-3 flex items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">
            {childLabel} di {parent.name}
          </h2>
          <p className="text-xs text-muted-foreground">
            {items.length} lokasi · {variantLabel} hari ini
          </p>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((c) => {
          const tzH = tzOffsetHours(c.tz);
          const t = calculatePrayerTimes({ y: today.y, m: today.m, d: today.d, lat: c.lat, lng: c.lng, tzHours: tzH });
          return (
            <Link
              key={c.slug}
              to={shortPath(variant, c) as never}
              className="group flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-3 transition-colors hover:border-prayer hover:bg-prayer/5"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{c.name}</div>
                <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <Sun className="h-3 w-3 text-prayer" /> Subuh <span className="font-mono tabular-nums">{t.subuh}</span>
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Moon className="h-3 w-3 text-prayer" /> Maghrib <span className="font-mono tabular-nums">{t.maghrib}</span>
                  </span>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-prayer" />
            </Link>
          );
        })}
      </div>
      {remaining > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-3 inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:border-prayer hover:bg-prayer/5"
        >
          Tampilkan semua {items.length} {childLabel.toLowerCase()} di {parent.name}
        </button>
      )}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemList) }} />
    </section>
  );
}
